// ==================== WHITEPAPER LEADS PAGE (ADMIN) ====================

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText,
  Users,
  Download,
  BookOpen,
  UserPlus,
  AlertCircle,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  Mail,
  Phone,
  Building2,
  Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { WHITEPAPERS } from '../../constants';
import { fadeInUp, staggerContainer } from '../../utils/animations';
import {
  whitepapersService,
  type WhitepaperLead,
  type WhitepaperLeadStatistics,
  type WhitepaperLeadListQuery,
} from '../../services';

// ==================== STAT CARD COMPONENT ====================

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
}

const StatCard = ({ title, value, icon }: StatCardProps) => (
  <motion.div variants={fadeInUp}>
    <Card className="relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-(--color-muted-gray)">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
        </div>
        <div className="p-3 rounded-xl bg-(--color-navy-light) text-(--color-signal-orange)">
          {icon}
        </div>
      </div>
    </Card>
  </motion.div>
);

// ==================== LEAD ROW COMPONENT ====================

const whitepaperTitle = (id: string) => WHITEPAPERS.find((w) => w.id === id)?.title ?? id;

const formatDateTime = (dateString: string) =>
  new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const LeadRow = ({ lead }: { lead: WhitepaperLead }) => (
  <motion.tr
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="border-b border-(--color-border-gray) hover:bg-(--color-navy-light)/50 transition-colors align-top"
  >
    <td className="py-4 px-4 whitespace-nowrap">
      <p className="font-medium text-white">{lead.fullName}</p>
      <p className="text-sm text-(--color-muted-gray) flex items-center gap-1.5 mt-1">
        <Building2 size={14} className="shrink-0" />
        {lead.company || '—'}
      </p>
    </td>
    <td className="py-4 px-4 whitespace-nowrap">
      <a
        href={`mailto:${lead.email}`}
        className="text-sm text-(--color-soft-gray) hover:text-(--color-signal-orange) flex items-center gap-1.5"
      >
        <Mail size={14} className="shrink-0" />
        {lead.email}
      </a>
      <a
        href={`tel:${lead.phone}`}
        className="text-sm text-(--color-soft-gray) hover:text-(--color-signal-orange) flex items-center gap-1.5 mt-1"
      >
        <Phone size={14} className="shrink-0" />
        {lead.phone}
      </a>
    </td>
    <td className="py-4 px-4 text-white font-semibold">{lead.downloadCount}</td>
    <td className="py-4 px-4 text-white font-semibold">{lead.readCount}</td>
    <td className="py-4 px-4">
      <div className="flex flex-wrap gap-1.5 max-w-56">
        {lead.whitepaperIds.map((id) => (
          <Badge key={id} variant="orange">{whitepaperTitle(id)}</Badge>
        ))}
      </div>
    </td>
    <td className="py-4 px-4 text-sm text-(--color-soft-gray) whitespace-nowrap">
      {formatDateTime(lead.createdAt)}
    </td>
    <td className="py-4 px-4 text-sm text-(--color-soft-gray) whitespace-nowrap">
      {formatDateTime(lead.lastAccessedAt)}
    </td>
  </motion.tr>
);

// ==================== MAIN PAGE COMPONENT ====================

const SORT_OPTIONS: { value: NonNullable<WhitepaperLeadListQuery['sortBy']>; label: string }[] = [
  { value: 'lastAccessedAt', label: 'Latest activity' },
  { value: 'createdAt', label: 'Newest leads' },
  { value: 'downloadCount', label: 'Most downloads' },
  { value: 'readCount', label: 'Most reads' },
  { value: 'fullName', label: 'Name' },
];

const WhitepaperLeadsPage = () => {
  const [leads, setLeads] = useState<WhitepaperLead[]>([]);
  const [statistics, setStatistics] = useState<WhitepaperLeadStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  // Filters
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState<NonNullable<WhitepaperLeadListQuery['sortBy']>>('lastAccessedAt');

  const [reloadKey, setReloadKey] = useState(0);

  // Callers set isLoading before changing the query; the effect only applies results,
  // and ignores responses that arrive after a newer query was started.
  useEffect(() => {
    let cancelled = false;

    whitepapersService
      .getLeads({
        page,
        limit,
        search: search || undefined,
        sortBy,
        sortOrder: sortBy === 'fullName' ? 'asc' : 'desc',
      })
      .then((response) => {
        if (cancelled) return;
        if (response.success && response.data) {
          setLeads(response.data.items);
          setTotalPages(response.data.meta?.totalPages || 1);
          setTotal(response.data.meta?.total || 0);
          setError(null);
        } else {
          setError(response.message || 'Failed to load whitepaper leads');
          setLeads([]);
        }
        setIsLoading(false);
      });

    whitepapersService.getLeadStatistics().then((response) => {
      if (!cancelled && response.success && response.data) {
        setStatistics(response.data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [page, limit, search, sortBy, reloadKey]);

  const changeQuery = (apply: () => void) => {
    setIsLoading(true);
    apply();
  };

  const handleSearch = () => {
    changeQuery(() => {
      setSearch(searchInput.trim());
      setPage(1);
    });
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6"
    >
      {/* Page Header */}
      <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white">Whitepaper Leads</h1>
          <p className="text-(--color-muted-gray) mt-1">
            People who downloaded or read a whitepaper
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => changeQuery(() => setReloadKey((key) => key + 1))}
          leftIcon={<RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </motion.div>

      {/* Statistics Cards */}
      {statistics && (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          <StatCard title="Total Leads" value={statistics.totalLeads} icon={<Users size={24} />} />
          <StatCard title="Downloads" value={statistics.totalDownloads} icon={<Download size={24} />} />
          <StatCard title="Online Reads" value={statistics.totalReads} icon={<BookOpen size={24} />} />
          <StatCard title="New (last 7 days)" value={statistics.newLast7Days} icon={<UserPlus size={24} />} />
        </motion.div>
      )}

      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-4 rounded-xl bg-(--color-error)/10 border border-(--color-error)/30"
          >
            <AlertCircle size={20} className="text-(--color-error)" />
            <p className="text-sm text-(--color-error) flex-1">{error}</p>
            <Button variant="ghost" size="sm" onClick={() => setError(null)}>
              Dismiss
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filters */}
      <motion.div variants={fadeInUp}>
        <Card>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="flex gap-2">
                <Input
                  placeholder="Search by name, email, phone or company..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  leftIcon={<Search size={16} />}
                />
                <Button variant="secondary" onClick={handleSearch}>
                  Search
                </Button>
              </div>
            </div>
            <div className="w-full md:w-52">
              <Select
                options={SORT_OPTIONS}
                value={sortBy}
                onChange={(value) =>
                  changeQuery(() => {
                    setSortBy(value as NonNullable<WhitepaperLeadListQuery['sortBy']>);
                    setPage(1);
                  })
                }
                placeholder="Sort by"
              />
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Leads Table */}
      <motion.div variants={fadeInUp}>
        <Card className="overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 size={32} className="text-(--color-signal-orange) animate-spin" />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12">
              <FileText size={48} className="text-(--color-muted-gray) mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No Leads Found</h3>
              <p className="text-(--color-muted-gray)">
                {search
                  ? 'Try adjusting your search'
                  : 'Nobody has downloaded or read a whitepaper yet.'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-(--color-border-gray)">
                      {['Name', 'Contact', 'Downloads', 'Reads', 'Whitepapers', 'First Seen', 'Last Activity'].map(
                        (heading) => (
                          <th
                            key={heading}
                            className="text-left py-3 px-4 text-sm font-medium text-(--color-muted-gray) whitespace-nowrap"
                          >
                            {heading}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <LeadRow key={lead.id} lead={lead} />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-(--color-border-gray)">
                  <p className="text-sm text-(--color-muted-gray)">
                    Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} leads
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => changeQuery(() => setPage((p) => Math.max(1, p - 1)))}
                      disabled={page === 1}
                      leftIcon={<ChevronLeft size={16} />}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-(--color-soft-gray)">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => changeQuery(() => setPage((p) => Math.min(totalPages, p + 1)))}
                      disabled={page === totalPages}
                      rightIcon={<ChevronRight size={16} />}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </motion.div>
    </motion.div>
  );
};

export { WhitepaperLeadsPage };
